
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Button,
  Stack,
  Typography,
  Skeleton,
  Card
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";

import BreadcrumbsNav from "../../components/common/BreadCrumbNav";
import ResidentialGridView from "./ResidentialGridView";
import ResidentialPropertiesCard from "../../components/common/ResidentialPropertiesCard";
import ResidentialFilter from "../../components/common/ResidentialFilter";
import { get_plot_list } from "../../api/ControllApi";
import FilterDataCard from "../../components/common/FilterDataCard";
import { useCachedApi } from "../../hooks/useCachedApi";
import { useSettings } from "../../context/Settings";
import DescriptionNote from "../../components/common/DescriptionNote";

export default function ResidentialProperty() {
  const navigate = useNavigate();
  const { requestApi } = useCachedApi();
  const { settings } = useSettings();
  const [view, setView] = useState("grid");
  const [plotData, setPlotData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [resp, setResp] = useState([])
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });

  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const fetchGeoPlots = async (filters = {}, page = 1) => {
    setLoading(true);

    try {
      const params = {};

      // only add a param if it has a value
      if (filters.country_id) params.country_id = filters.country_id;
      if (filters.state_id) params.state_id = filters.state_id;
      if (filters.city_id) params.city_id = filters.city_id;


      if (filters.plotType && filters.plotType.length > 0) {
        params['category_id[]'] = filters.plotType; // send array directly
      }



      if (filters.priceRange && filters.priceRange.length === 2) {
        // send only if user changed from undefined
        params.min_price = filters.priceRange[0];
        params.max_price = filters.priceRange[1];
      }

      if (filters.areaRange && filters.areaRange.length === 2) {
        params.min_sqft = filters.areaRange[0];
        params.max_sqft = filters.areaRange[1];
      }

      if (filters.searchQuery?.trim()) params.search = filters.searchQuery.trim();



      const cacheKey = `residential_plots_${JSON.stringify(params)}_${page}`;
      const apiCall = () =>
        Object.keys(params).length > 0 || page > 0
          ? get_plot_list(params, page)
          : get_plot_list();

      const res = await requestApi(cacheKey, apiCall);

      setResp(res?.data);
      setPlotData(res?.data?.plots || []);
      setPagination(
        res?.data?.pagination || { current_page: 1, last_page: 1 }
      );

    } catch (err) {
      setPlotData([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchGeoPlots(filters, page);
  }, [filters, page]);


  const handlePageChange = (page) => {
    setPage(page);
    setPagination((prev) => ({ ...prev, current_page: page }));
    window.scrollTo({ top: 0, behavior: "smooth" });
    // fetchGeoPlots({}, page);
  };
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);
  const PropertySkeleton = ({ index = 0 }) => {
    return (
      <Card
        sx={{
          borderRadius: 3,
          p: 2,
          width: "100%",
          opacity: 0,
          animation: `skelFadeIn 0.4s ease forwards`,
          animationDelay: `${index * 0.07}s`,
          "@keyframes skelFadeIn": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Skeleton variant="rectangular" height={160} animation="wave" sx={{ borderRadius: 2 }} />
        <Skeleton height={28} sx={{ mt: 1 }} animation="wave" />
        <Skeleton height={20} width="80%" animation="wave" />
        <Skeleton height={24} width="60%" sx={{ mt: 1 }} animation="wave" />
        <Skeleton height={40} width="100%" sx={{ mt: 1, borderRadius: 2 }} animation="wave" />
      </Card>
    );
  };


  return (
    <Container sx={{ py: 5 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <BreadcrumbsNav />
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate("/add-residential-property")}
          sx={{ borderRadius: '20px', px: 2, fontSize: '14px', fontWeight: 'bold' }}
        >
          Add Flats/Villas +
        </Button>
      </Box>
      {settings?.data?.plot_description && (
        <DescriptionNote description={settings.data.plot_description} />
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: 1.5 }}>
        <IconButton color={view === "grid" ? "primary" : "default"} onClick={() => setView("grid")}>
          <Box key={view === "grid" ? "grid-active" : "grid-idle"} component="span"
            sx={{ display: "flex", ...(view === "grid" && { animation: "iconPop 0.28s ease", "@keyframes iconPop": { "0%": { transform: "scale(0.55) rotate(-20deg)", opacity: 0.4 }, "65%": { transform: "scale(1.22)", opacity: 1 }, "100%": { transform: "scale(1) rotate(0deg)", opacity: 1 } } }) }}>
            <ViewModuleIcon />
          </Box>
        </IconButton>
        <IconButton color={view === "list" ? "primary" : "default"} onClick={() => setView("list")}>
          <Box key={view === "list" ? "list-active" : "list-idle"} component="span"
            sx={{ display: "flex", ...(view === "list" && { animation: "iconPop 0.28s ease", "@keyframes iconPop": { "0%": { transform: "scale(0.55) rotate(20deg)", opacity: 0.4 }, "65%": { transform: "scale(1.22)", opacity: 1 }, "100%": { transform: "scale(1) rotate(0deg)", opacity: 1 } } }) }}>
            <ViewListIcon />
          </Box>
        </IconButton>
      </Box>


      <Box
        sx={{
          display: { sm: "block", lg: "flex" },
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            width: { xs: "90%", sm: "60%", lg: 260 },
            mx: { xs: "auto", lg: 0 },
            flex: { lg: "0 0 260px" },
            position: { xs: "static", md: "sticky" },
            top: { lg: 110 },
            mb: { xs: 2, lg: 0 },
          }}
        >
          {/* <ResidentialFilter /> */}
          <FilterDataCard onFilterChange={handleFilterChange} isResidental={true} PropertyType={resp?.property_category} priceMax={resp?.price_max} priceMin={resp?.price_min} stateList={resp?.state_list} areaMax={resp?.sqft_max} areaMin={resp?.sqft_min} />
        </Box>

        <Box
          key={view}
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns:
              view === "grid"
                ? {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr",
                }
                : {
                  xs: "1fr",
                  sm: "1fr",
                  md: "1fr",
                  lg: "1fr",
                },
            gap: { xs: 1.5, md: 2 },
            ml: { xs: 0, lg: 6 },
            mt: { xs: 2, lg: 0 },
            px: 2,
            py: 2,
            "& > *": { height: "100%" },
            // container entrance
            animation: "viewEnter 0.35s cubic-bezier(0.34,1.2,0.64,1) both",
            "@keyframes viewEnter": {
              "0%": { opacity: 0, transform: "scale(0.97) translateY(14px)" },
              "60%": { opacity: 1, transform: "scale(1.005) translateY(-2px)" },
              "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
            },
            // stagger each direct child card
            "& > *": {
              animation: "cardSlideIn 0.42s cubic-bezier(0.34,1.2,0.64,1) both",
              height: "100%",
              "@keyframes cardSlideIn": {
                "0%": { opacity: 0, transform: "translateY(20px) scale(0.96)" },
                "70%": { opacity: 1, transform: "translateY(-3px) scale(1.01)" },
                "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
              },
            },
            "& > *:nth-of-type(1)": { animationDelay: "0.04s" },
            "& > *:nth-of-type(2)": { animationDelay: "0.09s" },
            "& > *:nth-of-type(3)": { animationDelay: "0.14s" },
            "& > *:nth-of-type(4)": { animationDelay: "0.19s" },
            "& > *:nth-of-type(5)": { animationDelay: "0.24s" },
            "& > *:nth-of-type(6)": { animationDelay: "0.29s" },
            "& > *:nth-of-type(n+7)": { animationDelay: "0.32s" },
          }}
        >
          {loading ? (
            Array.from(new Array(6)).map((_, index) => (
              <PropertySkeleton key={index} index={index} />
            ))
          ) : plotData.length > 0 ? (
            plotData.map((plot) =>
              view === "grid" ? (
                <ResidentialGridView
                  key={plot.id}
                  id={plot.id}
                  name={plot.property_name}
                  price={plot.price}
                  address={plot.location}
                  content={plot.about_property}
                  thumbnail={
                    plot.thumbnail ||
                    (plot.gallery_images && plot.gallery_images[0])
                  }
                  images={
                    plot.gallery_images?.length
                      ? plot.gallery_images
                      : plot.images || [plot.thumbnail]
                  }
                  facilities={plot.facilities || []}
                  nearby={plot.nearby_locations || []}
                  amenities={plot.amenitiesall || []}
                  perSqft={plot.area_sqft_price}
                  area={plot.area_sqft}
                  categoryname={plot.cate?.category_name}
                  description={plot.overview}
                  verify={plot?.verify_status}
                  country={plot.country}
                  soldStatus={plot?.sold_status}
                />
              ) : (
                <ResidentialPropertiesCard
                  key={plot.id}
                  id={plot.id}
                  name={plot.property_name}
                  price={plot.price}
                  address={plot.location}
                  content={plot.about_property}
                  thumbnail={
                    plot.thumbnail ||
                    (plot.gallery_images && plot.gallery_images[0])
                  }
                  images={
                    plot.gallery_images?.length
                      ? plot.gallery_images
                      : plot.images || [plot.thumbnail]
                  }
                  facilities={plot.facilities || []}
                  nearby={plot.nearby_locations || []}
                  amenities={plot.amenitiesall || []}
                  perSqft={plot.area_sqft_price}
                  area={plot.area}
                  categoryname={plot.cate?.category_name}
                  description={plot.about_property}
                  verify={plot?.verify_status}
                  country={plot.country}
                  soldStatus={plot?.sold_status}
                />
              )
            )
          ) : (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "60vh",
                textAlign: "center",
                width: "100%",
                py: 5,
              }}
            >
              <Box
                component="img"
                src="/assets/images/nodatafound.png"
                alt="No Data Found"
                sx={{ width: { xs: 320, sm: 350, md: 380 }, mb: 2 }}
              />
            </Box>
          )}

        </Box>
      </Box>

      {/* PAGINATION*/}

      {plotData.length > 0 && pagination.last_page > 1 && <Stack direction="row" spacing={1} justifyContent="center" mt={5}>
        {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(
          (page) => (
            <Button
              key={page}
              variant={
                page === pagination.current_page ? "contained" : "outlined"
              }
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Button>
          )
        )}
      </Stack>}
    </Container>
  );
}
